import { useMutation, useQueryClient } from "@tanstack/react-query"
import { notificationApiCalls } from "../api/notificationApi"
import {
  cancelNotificationQueries,
  findCachedNotification,
  invalidateNotificationCaches,
  NotificationMutationContext,
  patchAllLists,
  rollbackNotificationCaches,
  setUnreadCount,
  snapshotNotificationCaches,
} from "./cacheUtils"

/**
 * Action-button (or Dismiss) click → POST /{id}/acted. Optimistic: the CTA
 * emphasis clears immediately and the center-page item flips to its done
 * state; `actionRequiredCount` decrements; rolls back on error.
 */
export const useMarkActed = () => {
  const queryClient = useQueryClient()

  return useMutation<void, Error, string, NotificationMutationContext>({
    mutationFn: (notificationId) =>
      notificationApiCalls.markActed(notificationId),
    onMutate: async (notificationId) => {
      await cancelNotificationQueries(queryClient)
      const context = snapshotNotificationCaches(queryClient)

      const cached = findCachedNotification(queryClient, notificationId)
      // The backend's /{id}/acted also sets read_at, so acting/dismissing must
      // clear the unread badge too — decrement `count` (when it was unread) and
      // `actionRequiredCount` (when it still needed action).
      const wasUnread = cached ? cached.readAt === null : true
      const needsAction =
        cached?.kind === "action_required" && cached.actedAt === null

      setUnreadCount(queryClient, (previous) => ({
        ...previous,
        count: wasUnread ? Math.max(0, previous.count - 1) : previous.count,
        actionRequiredCount: needsAction
          ? Math.max(0, previous.actionRequiredCount - 1)
          : previous.actionRequiredCount,
      }))

      const now = new Date().toISOString()
      patchAllLists(queryClient, (item) =>
        item.id === notificationId
          ? {
              ...item,
              actedAt: item.actedAt ?? now,
              readAt: item.readAt ?? now,
            }
          : item,
      )

      return context
    },
    onError: (_error, _notificationId, context) => {
      rollbackNotificationCaches(queryClient, context)
    },
    onSettled: () => {
      invalidateNotificationCaches(queryClient)
    },
  })
}
